import api from '@/lib/api';

/** Gateway SMS Android: config, telefoni registrati, coda messaggi. */

export type SmsRelayMode = 'ovh' | 'relay' | 'relay+ovh';

export interface SmsRelayConfig {
  mode: SmsRelayMode;
  fallbackAfterMs: number;
  leaseMs: number;
  maxPollBatch: number;
  longPollMaxWaitMs: number;
  alertQueueDepth: number;
  alertDeviceSilentMs: number;
  /** Percentuale di batteria sotto cui il gateway genera un'allerta. 0 = spenta. */
  alertBatteryPercent: number;
  internationalAlertPerMonth: number;
}

export interface SmsRelayHeartbeat {
  battery: number;
  charging: boolean;
  simState: string;
  signalLevel?: number;
  sentLastHour: number;
  pendingAcks?: number;
  appVersion: string;
  at: string;
}

export interface SmsRelayDevice {
  id: string;
  name: string;
  keyPrefix: string;
  simNumber?: string;
  active: boolean;
  paused: boolean;
  maxPerHour: number;
  online: boolean;
  lastSeenAt?: string;
  lastHeartbeat?: SmsRelayHeartbeat;
  sentCount: number;
  failedCount: number;
  createdAt: string;
}

export type SmsMessageStatus =
  | 'queued'
  | 'claimed'
  | 'sent'
  | 'delivered'
  | 'failed'
  | 'expired'
  | 'canceled';

export type SmsPurpose = 'otp' | 'call' | 'notification' | 'marketing' | 'test';

export interface SmsRelayMessage {
  id: string;
  to: string;
  purpose: SmsPurpose;
  status: SmsMessageStatus;
  /** Assente per gli OTP: il corpo contiene il codice e non viene esposto. */
  body?: string;
  priority: number;
  attempts: number;
  maxAttempts: number;
  expiresAt: string;
  sentAt?: string;
  sentVia?: 'relay' | 'ovh';
  errorCode?: string;
  errorMessage?: string;
  segments?: number;
  createdAt: string;
}

export interface SmsRelayStats {
  counts: Partial<Record<SmsMessageStatus, number>>;
  oldestQueuedAt?: string;
  mode: SmsRelayMode;
}

/** Consumo SMS verso l'estero nel mese: nessun tetto, solo monitoraggio. */
export interface SmsIntlStats {
  month: string;
  items: Array<{ dial: string; sent: number; failed: number }>;
  total: { sent: number; failed: number };
  threshold: number;
}

export async function getConfig(): Promise<SmsRelayConfig> {
  const res = await api.get('/sms-relay/admin/config');
  return res.data.data as SmsRelayConfig;
}

export async function updateConfig(data: Partial<SmsRelayConfig>): Promise<SmsRelayConfig> {
  const res = await api.put('/sms-relay/admin/config', data);
  return res.data.data as SmsRelayConfig;
}

export async function getDevices(): Promise<SmsRelayDevice[]> {
  const res = await api.get('/sms-relay/admin/devices');
  return (res.data.data as { devices: SmsRelayDevice[] }).devices;
}

/** La chiave in chiaro torna solo qui: da mostrare subito e mai più. */
export async function createDevice(data: {
  name: string;
  simNumber?: string;
  maxPerHour?: number;
}): Promise<{ device: SmsRelayDevice; apiKey: string }> {
  const res = await api.post('/sms-relay/admin/devices', data);
  return res.data.data as { device: SmsRelayDevice; apiKey: string };
}

export async function updateDevice(
  id: string,
  data: { name?: string; simNumber?: string; active?: boolean; paused?: boolean; maxPerHour?: number },
): Promise<SmsRelayDevice> {
  const res = await api.patch(`/sms-relay/admin/devices/${id}`, data);
  return res.data.data as SmsRelayDevice;
}

export async function rotateDeviceKey(id: string): Promise<{ device: SmsRelayDevice; apiKey: string }> {
  const res = await api.post(`/sms-relay/admin/devices/${id}/rotate-key`);
  return res.data.data as { device: SmsRelayDevice; apiKey: string };
}

export async function deleteDevice(id: string): Promise<void> {
  await api.delete(`/sms-relay/admin/devices/${id}`);
}

export async function getMessages(params: {
  page?: number;
  status?: SmsMessageStatus | '';
  purpose?: SmsPurpose | '';
}): Promise<{ items: SmsRelayMessage[]; total: number; page: number; limit: number }> {
  const res = await api.get('/sms-relay/admin/messages', {
    params: {
      page: params.page ?? 1,
      status: params.status || undefined,
      purpose: params.purpose || undefined,
    },
  });
  return res.data.data as { items: SmsRelayMessage[]; total: number; page: number; limit: number };
}

export async function getStats(): Promise<SmsRelayStats> {
  const res = await api.get('/sms-relay/admin/stats');
  return res.data.data as SmsRelayStats;
}

export async function retryMessage(id: string, via: 'relay' | 'ovh' = 'relay'): Promise<SmsRelayMessage> {
  const res = await api.post(`/sms-relay/admin/messages/${id}/retry`, { via });
  return res.data.data as SmsRelayMessage;
}

export async function cancelMessage(id: string): Promise<SmsRelayMessage> {
  const res = await api.post(`/sms-relay/admin/messages/${id}/cancel`);
  return res.data.data as SmsRelayMessage;
}

export async function sendTest(to: string, body?: string): Promise<{ status: string }> {
  const res = await api.post('/sms-relay/admin/test', { to, body });
  return res.data.data as { status: string };
}

export async function getIntlStats(month?: string): Promise<SmsIntlStats> {
  const res = await api.get('/sms-relay/admin/intl-stats', {
    params: month ? { month } : undefined,
  });
  return res.data.data as SmsIntlStats;
}
